/**
 * Tracking analytics interfață client (public slug / tabletă).
 * Fire-and-forget — nu blochează UX-ul.
 */
import { getPublicClientSlug, parsePublicSlugFromPath } from './public-client.js?v=83.58';
import { getCurrentUser } from './auth.js?v=83.58';
import { trackClientInterfaceEventBackend } from './backend.js?v=83.58';

const SESSION_STORAGE_KEY = 'sj_client_analytics_session';
const INTERFACE_VIEW_KEY = 'sj_client_interface_viewed';

export const MODULE_LABELS = {
  interface: 'Scanări cod QR',
  menu: 'Meniu',
  wifi: 'WiFi',
  location: 'Locație',
  hours: 'Program',
  review: 'Recenzie',
  social: 'Social Media',
  social_instagram: 'Instagram',
  social_facebook: 'Facebook',
  social_tiktok: 'TikTok',
  images: 'Secțiunea editabilă',
  editable_section: 'Secțiuni editabile',
  reservation: 'Rezervare',
  community: 'Comunitate',
  logo: 'Logo',
  campaign: 'Campanii',
  loyalty: 'Fidelizare',
  event: 'Evenimente'
};

function isPublicQrSession() {
  return !!(getPublicClientSlug() || parsePublicSlugFromPath());
}

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `s${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    }
    return id;
  } catch (_) {
    return null;
  }
}

function getDeviceType() {
  try {
    const w = Math.min(window.innerWidth || 0, screen.width || 0);
    if (w > 0 && w < 600) return 'mobile';
    if (w >= 600 && w < 1024) return 'tablet';
    return 'desktop';
  } catch (_) {
    return 'unknown';
  }
}

function getTrackingPublicSlug() {
  return getPublicClientSlug() || parsePublicSlugFromPath() || null;
}

function getTrackingSource() {
  if (isPublicQrSession()) return 'public_slug';
  if (getCurrentUser()) return 'tablet_client';
  return null;
}

/** Pagină client vizibilă (slug public sau preview tabletă). */
export function isClientInterfaceContext() {
  if (isPublicQrSession()) return true;
  const clientPage = document.getElementById('clientPage');
  const communityPage = document.getElementById('communityPage');
  return !!(
    clientPage?.classList.contains('visible') || communityPage?.classList.contains('visible')
  );
}

function getPageId() {
  if (document.getElementById('communityPage')?.classList.contains('visible')) {
    return 'communityPage';
  }
  return 'clientPage';
}

/**
 * @param {string} moduleKey
 * @param {string} actionType
 * @param {Record<string, unknown>} [extraData]
 */
/** Ultimul moment numărat pe fiecare modul — un singur gest al clientului = o singură accesare. */
const lastTrackedAt = new Map();
const SAME_GESTURE_MS = 2000;

export function trackClientEvent(moduleKey, actionType, extraData = {}) {
  if (window.__SJ_DESK_FRAME) return;
  // Ex.: apăsarea pe „Meniu” trimitea „click” și apoi „gallery_open” → o deschidere era numărată de 2 ori.
  const key = String(moduleKey || '').trim();
  if (key && key !== 'interface') {
    const now = Date.now();
    const last = lastTrackedAt.get(key) || 0;
    if (now - last < SAME_GESTURE_MS) return;
    lastTrackedAt.set(key, now);
  }
  // previzualizarea din panoul admin (laptop) nu e o vizită reală
  if (/[?&]sj_preview=1/.test(window.location.search || '')) return;
  if (!isClientInterfaceContext()) return;
  const source = getTrackingSource();
  if (!source) return;

  const payload = {
    moduleKey: String(moduleKey || '').trim(),
    actionType: String(actionType || 'click').trim(),
    moduleLabel:
      String(extraData.moduleLabel || '').trim() ||
      MODULE_LABELS[moduleKey] ||
      moduleKey,
    publicSlug: getTrackingPublicSlug() || extraData.publicSlug || null,
    source,
    sessionId: getSessionId(),
    deviceType: getDeviceType(),
    pageId: extraData.pageId || getPageId()
  };

  trackClientInterfaceEventBackend(payload).catch(() => {});
}

/** O singură înregistrare „view” per sesiune / context. */
export function trackClientInterfaceViewOnce() {
  if (!isClientInterfaceContext()) return;
  const slug = getTrackingPublicSlug();
  const scope = slug ? `slug:${slug}` : 'tablet';
  const flagKey = `${INTERFACE_VIEW_KEY}:${scope}`;
  try {
    if (sessionStorage.getItem(flagKey) === '1') return;
    sessionStorage.setItem(flagKey, '1');
  } catch (_) {
    /* continue */
  }
  trackClientEvent('interface', 'view', { moduleLabel: MODULE_LABELS.interface });
}

const EXTERNAL_ACTION_MAP = {
  review: { moduleKey: 'review', actionType: 'click' },
  facebook: { moduleKey: 'social_facebook', actionType: 'click' },
  instagram: { moduleKey: 'social_instagram', actionType: 'click' },
  tiktok: { moduleKey: 'social_tiktok', actionType: 'click' },
  social: { moduleKey: 'social', actionType: 'click' }
};

const ACTION_TRACKING_MAP = {
  openLocationMenu: { moduleKey: 'menu', actionType: 'click' },
  openLocationMenuExternal: { moduleKey: 'menu', actionType: 'open_external' },
  openWifiPopup: { moduleKey: 'wifi', actionType: 'click' },
  copyWifiPassword: { moduleKey: 'wifi', actionType: 'copy' },
  copyWifiSsid: { moduleKey: 'wifi', actionType: 'copy' },
  openLocationMapPopup: { moduleKey: 'location', actionType: 'click' },
  openLocationHoursPopup: { moduleKey: 'hours', actionType: 'click' },
  openReservationPopup: { moduleKey: 'reservation', actionType: 'click' },
  openFeedbackPopup: { moduleKey: 'review', actionType: 'click' },
  playGame: { moduleKey: 'campaign', actionType: 'play' },
  showAutoRules: { moduleKey: 'campaign', actionType: 'rules_open' },
  startClaim: { moduleKey: 'campaign', actionType: 'claim_start' },
  openLoyaltyModuleOverlay: { moduleKey: 'loyalty', actionType: 'open' },
  submitLoyaltyPurchase: { moduleKey: 'loyalty', actionType: 'submit' },
  checkLoyaltyProgress: { moduleKey: 'loyalty', actionType: 'check' },
  openEventModuleOverlay: { moduleKey: 'event', actionType: 'open' },
  submitEventMessage: { moduleKey: 'event', actionType: 'submit' },
  openCommunityPage: { moduleKey: 'community', actionType: 'open' },
  submitCommunityLead: { moduleKey: 'community', actionType: 'submit' },
  openClientCampaignFromMenuMode: { moduleKey: 'campaign', actionType: 'click' }
};

/**
 * Mapare click-uri delegate prin data-action (inline-bridge).
 */
export function trackClientActionFromBridge(action, arg) {
  if (!action || !isClientInterfaceContext()) return;

  if (action === 'openExternalAction' && arg) {
    const mapped = EXTERNAL_ACTION_MAP[String(arg).toLowerCase()];
    if (mapped) {
      trackClientEvent(mapped.moduleKey, mapped.actionType);
    }
    return;
  }

  const mapped = ACTION_TRACKING_MAP[action];
  if (mapped) {
    trackClientEvent(mapped.moduleKey, mapped.actionType);
  }
}
